import type { Celda } from './tipos';

/**
 * Cola de persistencia de celdas: TODOS los guardados salen por acá, uno atrás
 * de otro.
 *
 * `guardarCeldas` escribe el array COMPLETO (last-write-wins en la DB) y en
 * CorridaView hay varios escritores concurrentes: el flush throttled del
 * disparo, el poll de completitud, el guardado final y el reintentar. Dos
 * requests en vuelo a la vez pueden llegar a Supabase en cualquier orden — si
 * el snapshot viejo aterriza último, pisa al nuevo: celdas ya disparadas o
 * completas vuelven a 'pendiente', y reabrir la corrida re-dispara trabajo ya
 * hecho (generaciones duplicadas, créditos gastados).
 *
 * Serializado, el orden de llegada ES el orden de encolado — y como cada
 * escritor encola lo más fresco que tiene en ese momento, el último guardado
 * siempre es el más nuevo. Un guardado caído no frena la cola ni pasa en
 * silencio (`onError`).
 */
export interface ColaGuardado {
  /** Deja el snapshot pendiente SIN persistir — lo levanta el próximo flush
   *  (así el disparo guarda máx ~1/s en vez de una vez por celda). */
  anotar(corridaId: string, celdas: Celda[]): void;
  /** Persiste el pendiente (si hay) detrás de todo lo ya encolado. */
  flush(): Promise<void>;
  /** Anotar + flush: guardado inmediato, en orden con todo lo anterior. */
  guardar(corridaId: string, celdas: Celda[]): Promise<void>;
}

export function crearColaGuardado(
  persistir: (corridaId: string, celdas: Celda[]) => Promise<void>,
  onError: (err: unknown) => void,
): ColaGuardado {
  let pendiente: { corridaId: string; celdas: Celda[] } | null = null;
  let cola: Promise<void> = Promise.resolve();

  const flush = (): Promise<void> => {
    const pend = pendiente;
    if (pend) {
      pendiente = null;
      cola = cola.then(() => persistir(pend.corridaId, pend.celdas)).catch(onError);
    }
    return cola;
  };

  const anotar = (corridaId: string, celdas: Celda[]): void => {
    // Snapshot de OTRA corrida esperando: se despacha antes de pisarlo — el
    // pendiente solo puede colapsar snapshots de la MISMA corrida (donde el
    // nuevo siempre contiene al viejo).
    if (pendiente && pendiente.corridaId !== corridaId) void flush();
    pendiente = { corridaId, celdas };
  };

  return {
    anotar,
    flush,
    guardar(corridaId, celdas) {
      anotar(corridaId, celdas);
      return flush();
    },
  };
}
